import { mergeAttributes, Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";

import { SnippetBlockView } from "./snippet-node-view";

export interface SnippetBlockAttrs {
  snippet_id: string;
  display_title?: string;
}

export interface SnippetBlockOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    snippetBlock: {
      insertSnippetBlock: (attrs: SnippetBlockAttrs) => ReturnType;
    };
  }
}

/**
 * `snippet-block` ProseMirror node.
 *
 * Plan 04 §10 + Phase E (Task 38). Backend persistence is the same
 * `editor.getJSON()` envelope that `variable-mention` (plan 01) uses — a
 * step body's ProseMirror tree is stored verbatim and re-rendered on the
 * server via `app.services.outreach.renderer`.
 *
 * Block-level atom: snippets render as a self-contained card surface in
 * the step editor and cannot be edited inline. The `display_title` attr
 * is denormalized at insert time so the chip can render the snippet's
 * title without an extra fetch on every render. The renderer treats
 * `snippet_id` as the source of truth — the title is purely cosmetic.
 *
 * `parseHTML`/`renderHTML` round-trip the dataset attrs so clipboard
 * copy/paste between editors keeps the node intact. The interactive
 * chip lives in `./snippet-node-view` and is attached via
 * `addNodeView`.
 */
export const SnippetBlock = Node.create<SnippetBlockOptions>({
  name: "snippet-block",

  inline: false,
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      snippet_id: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-snippet-id") ?? "",
        renderHTML: (attrs) => ({ "data-snippet-id": attrs.snippet_id }),
      },
      display_title: {
        default: undefined,
        parseHTML: (el) => el.getAttribute("data-display-title") ?? undefined,
        renderHTML: (attrs) => {
          if (typeof attrs.display_title !== "string") return {};
          return { "data-display-title": attrs.display_title };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="snippet-block"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(
        { "data-type": "snippet-block", contenteditable: "false" },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
    ];
  },

  addCommands() {
    return {
      insertSnippetBlock:
        (attrs: SnippetBlockAttrs) =>
        ({ chain }) =>
          chain()
            .focus()
            .insertContent({ type: this.name, attrs })
            .run(),
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(SnippetBlockView);
  },
});
